import { api } from '@/lib/api';
import type {
  ExecutiveSummary,
  AlertSummary,
  AlertTrend,
  AlertDistribution,
  AlertPattern,
  DeviceHealthSummary,
  DeviceTrend,
  FirmwareDistribution,
  DeviceAttention,
  CustomerHealth,
  CustomerHealthSummary,
  CustomerDetail,
  ComplianceSummary,
  ComplianceTrend,
  ComplianceGap,
  CalibrationItem,
  OperationsSummary,
  TicketTrend,
  TicketCategory,
  TicketAging,
  SlaComplianceItem,
  WorkloadItem,
  CommunicationsOverview,
  CommunicationsTrend,
  EmailStats,
  EmailDomain,
  EmailBounces,
  SmsStats,
  SmsCountry,
  SmsError,
  CallsStats,
  AlertSlaStats,
  AlertSendoutMethod,
  AccountHealthOverviewResponse,
  AccountDashboard,
  AccountHealthHistoryResponse,
} from '../types';

type RawAccountDashboardResponse = {
  account: {
    id: string;
    accountNumber: string;
    name: string;
    status: string;
    tier: string | null;
  };
  health: {
    score: number | null;
    subscriptionHealthScore: number | null;
    paymentHealthScore: number | null;
    engagementHealthScore: number | null;
    supportHealthScore: number | null;
  } | null;
  metrics: {
    arr: string;
    mrr: string;
    activeSensors: number;
    inactiveSensors: number;
    totalSites: number;
    openTickets: number;
    criticalTickets: number;
  };
  subscription?: {
    summary?: {
      active?: number;
    };
  };
  sites?: unknown[];
  [key: string]: unknown;
};

export function transformAccountDashboardResponse(
  raw: RawAccountDashboardResponse
): AccountDashboard {
  return {
    ...raw,
    healthMetrics: raw.health ? {
      healthScore: raw.health.score,
      subscriptionHealthScore: raw.health.subscriptionHealthScore,
      paymentHealthScore: raw.health.paymentHealthScore,
      engagementHealthScore: raw.health.engagementHealthScore,
      supportHealthScore: raw.health.supportHealthScore,
      arr: raw.metrics.arr ?? null,
      mrr: raw.metrics.mrr ?? null,
      activeSensorsCount: raw.metrics.activeSensors ?? null,
      inactiveSensorsCount: raw.metrics.inactiveSensors ?? null,
      totalSitesCount: raw.metrics.totalSites ?? null,
      openTicketsCount: raw.metrics.openTickets ?? null,
      criticalTicketsCount: raw.metrics.criticalTickets ?? null,
      avgTicketResolutionHours: null,
      overdueInvoicesCount: null,
      overdueAmount: null,
      daysSalesOutstanding: null,
      daysUntilSubscriptionExpiry: null,
      activeSubscriptionsCount: raw.subscription?.summary?.active ?? null,
      computedAt: null,
    } : null,
  };
}

// =============================================================================
// Executive Overview API
// =============================================================================

export async function getExecutiveSummary(refresh = false): Promise<ExecutiveSummary> {
  const response = await api.get<{ success: boolean; data: ExecutiveSummary }>('/v4/intelligence/executive/summary', {
    refresh: refresh ? 'true' : undefined,
  });
  return response.data;
}

export async function getPlatformMetrics(refresh = false) {
  const response = await api.get<{ success: boolean; data: unknown }>('/v4/intelligence/executive/platform', {
    refresh: refresh ? 'true' : undefined,
  });
  return response.data;
}

// =============================================================================
// Alert Intelligence API
// =============================================================================

export async function getAlertSummary(
  period: 'today' | 'week' | 'month' | 'quarter' = 'week',
  refresh = false
): Promise<AlertSummary> {
  const response = await api.get<{ success: boolean; data: AlertSummary }>('/v4/intelligence/alerts/summary', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  return response.data;
}

export async function getAlertTrends(
  period: 'day' | 'week' | 'month' = 'week',
  refresh = false
): Promise<AlertTrend[]> {
  const response = await api.get<{ success: boolean; data: AlertTrend[] }>('/v4/intelligence/alerts/trends', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  return response.data || [];
}

export async function getAlertDistribution(
  period: 'today' | 'week' | 'month' | 'quarter' = 'week',
  refresh = false
): Promise<AlertDistribution[]> {
  const response = await api.get<{ success: boolean; data: { distribution: AlertDistribution[] } }>('/v4/intelligence/alerts/distribution', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  return response.data?.distribution || [];
}

export async function getAlertResponseTimes(refresh = false) {
  return api.get('/v4/intelligence/alerts/response-times', {
    refresh: refresh ? 'true' : undefined,
  });
}

export async function getAlertPatterns(
  period: 'today' | 'week' | 'month' | 'quarter' = 'week',
  refresh = false
): Promise<AlertPattern[]> {
  const response = await api.get<{ success: boolean; data: { patterns: AlertPattern[] } }>('/v4/intelligence/alerts/patterns', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  return response.data?.patterns || [];
}

// =============================================================================
// Device Health API
// =============================================================================

export async function getDeviceHealthSummary(refresh = false): Promise<DeviceHealthSummary> {
  const response = await api.get<{ success: boolean; data: DeviceHealthSummary }>('/v4/intelligence/devices/health', {
    refresh: refresh ? 'true' : undefined,
  });
  return response.data;
}

export async function getDeviceTrends(
  period: 'day' | 'week' | 'month' = 'week',
  refresh = false
): Promise<DeviceTrend> {
  const response = await api.get<{ success: boolean; data: DeviceTrend }>('/v4/intelligence/devices/battery-trends', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  return response.data;
}

export async function getFirmwareDistribution(refresh = false): Promise<FirmwareDistribution[]> {
  const response = await api.get<{
    success: boolean;
    data: {
      distribution: Array<{ firmware_version: string; device_count: number; percentage: number }>
    }
  }>('/v4/intelligence/devices/firmware-distribution', {
    refresh: refresh ? 'true' : undefined,
  });
  // Transform backend response to frontend type
  return (response.data?.distribution || []).map(item => ({
    version: item.firmware_version,
    count: item.device_count,
    percentage: item.percentage,
  }));
}

export async function getDevicesNeedingAttention(refresh = false): Promise<DeviceAttention[]> {
  const response = await api.get<{
    success: boolean;
    data: {
      devices: Array<{
        _id: string;
        name?: string;
        active_flag?: boolean;
        battery_percentage?: number;
        rssi?: number;
        site_name?: string;
        issues?: Array<{ type: string; severity: string }>;
      }>;
      summary?: {
        total_devices: number;
        devices_with_issues: number;
        by_issue_type: Record<string, number>;
      };
    };
  }>('/v4/intelligence/devices/attention-needed', {
    refresh: refresh ? 'true' : undefined,
  });
  // Transform backend response to frontend type
  return (response.data?.devices || []).map(device => ({
    device_id: device._id,
    device_name: device.name,
    site_name: device.site_name,
    battery_level: device.battery_percentage,
    signal_strength: device.rssi,
    is_offline: device.active_flag === false,
    battery_low: device.issues?.some(i => i.type === 'low_battery' || i.type === 'critical_battery'),
    signal_weak: device.issues?.some(i => i.type === 'weak_signal'),
    firmware_pending: device.issues?.some(i => i.type === 'firmware_outdated'),
  }));
}

// =============================================================================
// Customer Health API
// =============================================================================

export async function getCustomerHealthSummary(refresh = false): Promise<CustomerHealthSummary> {
  const response = await api.get<{ success: boolean; data: CustomerHealthSummary }>('/v4/intelligence/customers/health-scores', {
    refresh: refresh ? 'true' : undefined,
  });
  return response.data;
}

export interface CustomerHealthSummaryResponse {
  total: number;
  healthy: number;
  warning: number;
  critical: number;
  unknown?: number;
}

export interface CustomerHealthListParams {
  page?: number;
  pageSize?: number;
  risk?: 'all' | 'healthy' | 'warning' | 'critical';
  search?: string;
  refresh?: boolean;
}

export async function getCustomerHealthList(
  params: CustomerHealthListParams = {}
): Promise<{ customers: CustomerHealth[]; total: number; summary?: CustomerHealthSummaryResponse }> {
  const { page = 0, pageSize = 25, risk, search, refresh = false } = params;

  // Backend uses limit/offset instead of pageNumber/pageLength
  // Backend returns "organisations" not "customers"
  const response = await api.get<{
    success: boolean;
    data: {
      organisations?: CustomerHealth[];
      customers?: CustomerHealth[];
      summary?: CustomerHealthSummaryResponse;
      total: number;
    };
  }>('/v4/intelligence/customers/list', {
    limit: pageSize,
    offset: page * pageSize,
    risk: risk && risk !== 'all' ? risk : undefined,
    search: search || undefined,
    refresh: refresh ? 'true' : undefined,
  });
  // Handle both field names for backwards compatibility (backend returns "organisations")
  const customers = response.data?.organisations || response.data?.customers || [];
  return {
    customers,
    total: response.data?.total || 0,
    summary: response.data?.summary,
  };
}

export async function getCustomerDetail(
  customerId: string,
  refresh = false
): Promise<CustomerDetail> {
  const response = await api.get<{ success: boolean; data: CustomerDetail }>(`/v4/intelligence/customers/${customerId}/detail`, {
    refresh: refresh ? 'true' : undefined,
  });
  return response.data;
}

export async function triggerCustomerHealthRecalculation(
  customerId: string
): Promise<{ health_score: number; risk_level: string }> {
  const response = await api.post<{
    success: boolean;
    data: { health_score: number; risk_level: string };
  }>(`/v4/intelligence/customers/${customerId}/compute`, {});
  return response.data;
}

// =============================================================================
// Account Health API (V4 PostgreSQL)
// =============================================================================

export async function getAllAccountNames(): Promise<{ id: string; name: string }[]> {
  const PAGE_SIZE = 100;
  const all: { id: string; name: string }[] = [];
  let pageNumber = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const response = await api.get<{ accounts: { id: string; name: string }[]; total: number }>(
      '/v4/accounts',
      { pageNumber, pageLength: PAGE_SIZE, sortKey: 'name', sortDirection: 1 },
    );
    const accounts = response.accounts || [];
    for (const a of accounts) {
      all.push({ id: a.id, name: a.name });
    }
    if (accounts.length < PAGE_SIZE) break;
    pageNumber++;
  }

  return all;
}

export async function getAccountHealthOverview(params: {
  page?: number;
  pageSize?: number;
  risk?: string;
  search?: string;
  sortKey?: string;
  sortDirection?: 1 | -1;
} = {}): Promise<AccountHealthOverviewResponse> {
  const { page = 0, pageSize = 25, risk, search, sortKey, sortDirection } = params;
  return api.get<AccountHealthOverviewResponse>('/v4/accounts/health-overview', {
    pageNumber: page,
    pageLength: pageSize,
    risk: risk && risk !== 'all' ? risk : undefined,
    search: search || undefined,
    sortKey,
    sortDirection,
  });
}

export async function getAccountDashboard(accountId: string): Promise<AccountDashboard> {
  const response = await api.get<RawAccountDashboardResponse>(`/v4/accounts/${accountId}/dashboard`);
  return transformAccountDashboardResponse(response);
}

export async function getAccountHealthHistory(accountId: string, days = 30) {
  return api.get<AccountHealthHistoryResponse>(`/v4/accounts/${accountId}/health-history`, { days });
}

// =============================================================================
// Compliance API
// =============================================================================

export async function getComplianceSummary(refresh = false): Promise<ComplianceSummary> {
  const response = await api.get<{ success: boolean; data: ComplianceSummary }>('/v4/intelligence/compliance/score', {
    refresh: refresh ? 'true' : undefined,
  });
  return response.data;
}

export async function getComplianceTrends(
  period: 'week' | 'month' | 'quarter' = 'month',
  refresh = false
): Promise<ComplianceTrend[]> {
  const response = await api.get<{
    success: boolean;
    data: {
      daily_logs: Array<{ date: string; compliance_rate: number }>;
      calibration: { compliance_rate: number };
    }
  }>('/v4/intelligence/compliance/trends', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  // Transform backend response to frontend format
  const calibrationRate = response.data?.calibration?.compliance_rate || 0;
  return (response.data?.daily_logs || []).map(item => ({
    date: item.date,
    daily_log_rate: item.compliance_rate,
    calibration_rate: calibrationRate,
  }));
}

export async function getCalibrationDue(refresh = false): Promise<CalibrationItem[]> {
  const response = await api.get<{
    success: boolean;
    data: {
      sensors: Array<{ name: string; site_name: string; calibration_due_date: string; status?: string }>
    }
  }>('/v4/intelligence/compliance/calibration-due', {
    refresh: refresh ? 'true' : undefined,
  });
  // Backend returns { sensors: [...], summary: {...} }, transform to expected format
  return (response.data?.sensors || []).map(s => ({
    sensor_name: s.name,
    site_name: s.site_name,
    due_date: s.calibration_due_date,
    status: s.status,
  }));
}

export async function getComplianceGaps(refresh = false): Promise<ComplianceGap[]> {
  const response = await api.get<{
    success: boolean;
    data: {
      gaps: Array<{ severity: 'critical' | 'warning' | 'info'; type: string; description: string; affected_count: number }>
    }
  }>('/v4/intelligence/compliance/gaps', {
    refresh: refresh ? 'true' : undefined,
  });
  // Transform backend severity to frontend severity
  const severityMap: Record<string, 'high' | 'medium' | 'low'> = {
    critical: 'high',
    warning: 'medium',
    info: 'low',
  };
  return (response.data?.gaps || []).map(g => ({
    severity: severityMap[g.severity] || 'low',
    type: g.type,
    description: g.description,
    count: g.affected_count,
  }));
}

// =============================================================================
// Operations API
// =============================================================================

export async function getOperationsSummary(refresh = false): Promise<OperationsSummary> {
  const response = await api.get<{ success: boolean; data: OperationsSummary }>('/v4/intelligence/operations/tickets/summary', {
    refresh: refresh ? 'true' : undefined,
  });
  return response.data;
}

export async function getTicketTrends(
  period: 'day' | 'week' | 'month' = 'week',
  refresh = false
): Promise<TicketTrend[]> {
  const response = await api.get<{ success: boolean; data: TicketTrend[] }>('/v4/intelligence/operations/tickets/trends', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  return response.data || [];
}

export async function getTicketsByCategory(refresh = false): Promise<TicketCategory[]> {
  const response = await api.get<{ success: boolean; data: TicketCategory[] }>('/v4/intelligence/operations/tickets/by-category', {
    refresh: refresh ? 'true' : undefined,
  });
  return response.data || [];
}

export async function getTicketAging(refresh = false): Promise<TicketAging[]> {
  const response = await api.get<{ success: boolean; data: TicketAging[] }>('/v4/intelligence/operations/tickets/aging', {
    refresh: refresh ? 'true' : undefined,
  });
  return response.data || [];
}

export async function getSlaCompliance(refresh = false): Promise<SlaComplianceItem[]> {
  const response = await api.get<{ success: boolean; data: SlaComplianceItem[] }>('/v4/intelligence/operations/sla/compliance', {
    refresh: refresh ? 'true' : undefined,
  });
  return response.data || [];
}

export async function getWorkloadAnalysis(refresh = false): Promise<WorkloadItem[]> {
  const response = await api.get<{ success: boolean; data: WorkloadItem[] }>('/v4/intelligence/operations/workload', {
    refresh: refresh ? 'true' : undefined,
  });
  return response.data || [];
}

// =============================================================================
// Communications API
// =============================================================================

export type PeriodFilter = 'day' | 'week' | 'month' | 'quarter';

export async function getCommunicationsOverview(
  period: PeriodFilter = 'week',
  refresh = false
): Promise<CommunicationsOverview> {
  const response = await api.get<{ success: boolean; data: CommunicationsOverview }>('/communications/dashboard/overview', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  return response.data;
}

export async function getCommunicationsTrends(
  period: PeriodFilter = 'week',
  refresh = false
): Promise<CommunicationsTrend[]> {
  const response = await api.get<{ success: boolean; data: CommunicationsTrend[] }>('/communications/dashboard/trends', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  return response.data || [];
}

// Email Analytics
export async function getEmailDeliveryStats(
  period: PeriodFilter = 'week',
  refresh = false
): Promise<EmailStats | null> {
  const response = await api.get<{ success: boolean; data: EmailStats | EmailStats[] }>('/communications/analytics/email/delivery-stats', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  // API may return array, extract first element (return null if empty)
  const data = response.data;
  if (Array.isArray(data)) {
    return data.length > 0 ? data[0] : null;
  }
  return data;
}

export async function getEmailByDomain(
  period: PeriodFilter = 'week',
  refresh = false
): Promise<EmailDomain[]> {
  const response = await api.get<{ success: boolean; data: EmailDomain[] }>('/communications/analytics/email/by-domain', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  return response.data || [];
}

export async function getEmailBounceBreakdown(
  period: PeriodFilter = 'week',
  refresh = false
): Promise<EmailBounces | null> {
  const response = await api.get<{ success: boolean; data: EmailBounces | EmailBounces[] }>('/communications/analytics/email/bounce-breakdown', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  const data = response.data;
  if (Array.isArray(data)) {
    return data.length > 0 ? data[0] : null;
  }
  return data;
}

// SMS Analytics
export async function getSmsDeliveryStats(
  period: PeriodFilter = 'week',
  refresh = false
): Promise<SmsStats | null> {
  const response = await api.get<{ success: boolean; data: SmsStats | SmsStats[] }>('/communications/analytics/sms/delivery-stats', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  const data = response.data;
  if (Array.isArray(data)) {
    return data.length > 0 ? data[0] : null;
  }
  return data;
}

export async function getSmsByCountry(
  period: PeriodFilter = 'week',
  refresh = false
): Promise<SmsCountry[]> {
  const response = await api.get<{ success: boolean; data: SmsCountry[] }>('/communications/analytics/sms/by-country', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  return response.data || [];
}

export async function getSmsErrorBreakdown(
  period: PeriodFilter = 'week',
  refresh = false
): Promise<SmsError[]> {
  const response = await api.get<{ success: boolean; data: SmsError[] }>('/communications/analytics/sms/error-breakdown', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  return response.data || [];
}

// Calls Analytics
export async function getCallsOutcomeStats(
  period: PeriodFilter = 'week',
  refresh = false
): Promise<CallsStats | null> {
  const response = await api.get<{ success: boolean; data: CallsStats | CallsStats[] }>('/communications/analytics/calls/outcome-stats', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  const data = response.data;
  if (Array.isArray(data)) {
    return data.length > 0 ? data[0] : null;
  }
  return data;
}

// Alert Sendouts Analytics
export async function getAlertSlaCompliance(
  period: PeriodFilter = 'week',
  refresh = false
): Promise<AlertSlaStats | null> {
  const response = await api.get<{ success: boolean; data: AlertSlaStats | AlertSlaStats[] }>('/communications/dashboard/alerts/sla-compliance', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  const data = response.data;
  if (Array.isArray(data)) {
    return data.length > 0 ? data[0] : null;
  }
  return data;
}

export async function getAlertSendoutsByMethod(
  period: PeriodFilter = 'week',
  refresh = false
): Promise<AlertSendoutMethod[]> {
  const response = await api.get<{ success: boolean; data: AlertSendoutMethod[] }>('/communications/dashboard/alerts/by-method', {
    period,
    refresh: refresh ? 'true' : undefined,
  });
  return response.data || [];
}

// =============================================================================
// Battery Monitoring API
// =============================================================================

import type {
  BatteryMonitoringResponse,
  BatteryMonitoringFilters,
} from '../types';

// Internal ATEK accounts excluded from battery monitoring
const EXCLUDED_ACCOUNT_NAMES = ['_ATEK_', '_ATEK_RnD_'];

export interface BatteryAccountOption {
  id: string; // PG UUID
  name: string;
  mongoOrganisationId: string | null;
}

/**
 * Fetch customer accounts for the account filter dropdown.
 * Paginates through V4 API, excludes internal ATEK accounts.
 */
export async function getBatteryMonitoringAccounts(): Promise<BatteryAccountOption[]> {
  const PAGE_SIZE = 100;
  const allAccounts: BatteryAccountOption[] = [];
  let pageNumber = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const response = await api.get<{
      accounts: Array<{ id: string; name: string; mongoOrganisationId: string | null }>;
      total: number;
    }>('/v4/accounts', {
      pageNumber,
      pageLength: PAGE_SIZE,
      sortKey: 'name',
      sortDirection: 1,
      type: 'customer',
    });

    const accounts = response.accounts || [];
    for (const account of accounts) {
      allAccounts.push({
        id: account.id,
        name: account.name,
        mongoOrganisationId: account.mongoOrganisationId,
      });
    }

    if (accounts.length < PAGE_SIZE) break;
    pageNumber++;
  }

  return allAccounts.filter(a => !EXCLUDED_ACCOUNT_NAMES.includes(a.name));
}

export async function getBatteryMonitoringData(
  filters: BatteryMonitoringFilters = {}
): Promise<BatteryMonitoringResponse> {
  const params: Record<string, string | number | undefined> = {};

  if (filters.organisation) params.organisation = filters.organisation;
  if (filters.site) params.site = filters.site;
  if (filters.voltage_min !== undefined) params.voltage_min = filters.voltage_min;
  if (filters.voltage_max !== undefined) params.voltage_max = filters.voltage_max;
  if (filters.base_color) params.base_color = filters.base_color;
  if (filters.power_source && filters.power_source !== 'all') params.power_source = filters.power_source;
  if (filters.status && filters.status !== 'all') params.status = filters.status;
  if (filters.search) params.search = filters.search;
  if (filters.page) params.page = filters.page;
  if (filters.limit) params.limit = filters.limit;
  if (filters.sort_by) params.sort_by = filters.sort_by;
  if (filters.sort_order) params.sort_order = filters.sort_order;

  // Always exclude internal accounts
  params.exclude_org_names = EXCLUDED_ACCOUNT_NAMES.join(',');

  const response = await api.get<{ success: boolean } & BatteryMonitoringResponse>(
    '/v4/intelligence/devices/battery-monitoring',
    params
  );
  return {
    items: response.items || [],
    summary: response.summary || {
      total: 0,
      critical_count: 0,
      low_count: 0,
      warning_count: 0,
      healthy_count: 0,
      external_power_count: 0,
      by_base_color: [],
    },
    pagination: response.pagination || { page: 1, limit: 25, total: 0, total_pages: 0 },
  };
}

// =============================================================================
// Cache Management
// =============================================================================

export async function invalidateCache(): Promise<void> {
  await api.post('/v4/intelligence/cache/invalidate', {});
}

// =============================================================================
// Deal Insights API
// =============================================================================

import type {
  DealSummary,
  DealByStage,
  DealByPipeline,
  DealByType,
  DealTrend,
  TopDeal,
  ClosingSoonDeal,
  OwnerPerformance,
  DealPipeline,
  DealStage,
} from '../types';

export interface DealFilters {
  pipeline?: string;
  stage?: string;
  dateFrom?: string;
  dateTo?: string;
  salesManagerId?: string;
}

export async function getDealSummary(filters: DealFilters = {}): Promise<DealSummary> {
  const response = await api.get<{ success: boolean; data: DealSummary }>('/v4/intelligence/deals/summary', {
    pipeline: filters.pipeline || undefined,
    stage: filters.stage || undefined,
    dateFrom: filters.dateFrom || undefined,
    dateTo: filters.dateTo || undefined,
    salesManagerId: filters.salesManagerId || undefined,
  });
  return response.data;
}

export async function getDealsByStage(pipeline?: string): Promise<DealByStage[]> {
  const response = await api.get<{ success: boolean; data: DealByStage[] }>('/v4/intelligence/deals/by-stage', {
    pipeline: pipeline || undefined,
  });
  return response.data || [];
}

export async function getDealsByType(pipeline?: string): Promise<DealByType[]> {
  const response = await api.get<{ success: boolean; data: DealByType[] }>('/v4/intelligence/deals/by-type', {
    pipeline: pipeline || undefined,
  });
  return response.data || [];
}

export async function getDealsByPipeline(): Promise<DealByPipeline[]> {
  const response = await api.get<{ success: boolean; data: DealByPipeline[] }>('/v4/intelligence/deals/by-pipeline');
  return response.data || [];
}

export async function getDealTrends(
  period: 'week' | 'month' | 'quarter' = 'month',
  pipeline?: string
): Promise<DealTrend[]> {
  const response = await api.get<{ success: boolean; data: DealTrend[] }>('/v4/intelligence/deals/trends', {
    period,
    pipeline: pipeline || undefined,
  });
  return response.data || [];
}

export async function getTopDeals(limit = 10, pipeline?: string): Promise<TopDeal[]> {
  const response = await api.get<{ success: boolean; data: TopDeal[] }>('/v4/intelligence/deals/top', {
    limit,
    pipeline: pipeline || undefined,
  });
  return response.data || [];
}

export async function getDealsClosingSoon(days = 30, pipeline?: string): Promise<ClosingSoonDeal[]> {
  const response = await api.get<{ success: boolean; data: ClosingSoonDeal[] }>('/v4/intelligence/deals/closing-soon', {
    days,
    pipeline: pipeline || undefined,
  });
  return response.data || [];
}

export async function getDealPipelines(): Promise<DealPipeline[]> {
  const response = await api.get<{ success: boolean; data: DealPipeline[] }>('/v4/intelligence/deals/pipelines');
  return response.data || [];
}

export async function getDealStages(pipeline?: string): Promise<DealStage[]> {
  const response = await api.get<{ success: boolean; data: DealStage[] }>('/v4/intelligence/deals/stages', {
    pipeline: pipeline || undefined,
  });
  return response.data || [];
}

export async function getOwnerPerformance(): Promise<OwnerPerformance[]> {
  const response = await api.get<{ success: boolean; data: OwnerPerformance[] }>('/v4/intelligence/deals/owner-performance');
  return response.data || [];
}

// =============================================================================
// Calibration Certificates API
// =============================================================================

import type {
  CalibrationCertificate,
  CalibrationCertificateFilters,
  CalibrationCertificatesResponse,
  CalibrationSummary,
  CalibrationOrganisation,
  CalibrationCertificateDetail,
  SavedView,
  SavedViewsResponse,
  CreateSavedViewInput,
  UpdateSavedViewInput,
} from '../types';

export async function getCalibrationCertificates(
  filters: CalibrationCertificateFilters = {}
): Promise<CalibrationCertificatesResponse> {
  const params: Record<string, string | number | boolean | undefined> = {};

  if (filters.accountId) params.accountId = filters.accountId;
  if (filters.organisationId) params.organisationId = filters.organisationId;
  if (filters.dueInDays) params.dueInDays = filters.dueInDays;
  if (filters.dueDateFrom) params.dueDateFrom = filters.dueDateFrom;
  if (filters.dueDateTo) params.dueDateTo = filters.dueDateTo;
  if (filters.includeOverdue !== undefined) params.includeOverdue = filters.includeOverdue;
  if (filters.status) {
    // Support both single status and array of statuses
    params.status = Array.isArray(filters.status) ? filters.status.join(',') : filters.status;
  }
  if (filters.search) params.search = filters.search;
  if (filters.locked !== undefined) params.locked = filters.locked;
  if (filters.showAll) params.showAll = true;
  if (filters.columnFilters && Object.values(filters.columnFilters).some(Boolean)) {
    params.columnFilters = JSON.stringify(filters.columnFilters);
  }
  if (filters.pageNumber !== undefined) params.pageNumber = filters.pageNumber;
  if (filters.pageLength !== undefined) params.pageLength = filters.pageLength;
  if (filters.sortKey) params.sortKey = filters.sortKey;
  if (filters.sortDirection) params.sortDirection = filters.sortDirection;

  return api.get<CalibrationCertificatesResponse>('/v4/calibration-certificates', params);
}

export async function getCalibrationCertificateSummary(
  options?: { organisationId?: string; accountId?: string; showAll?: boolean }
): Promise<CalibrationSummary> {
  const params: Record<string, string | boolean | undefined> = {};
  if (options?.organisationId) params.organisationId = options.organisationId;
  if (options?.accountId) params.accountId = options.accountId;
  if (options?.showAll) params.showAll = true;
  return api.get<CalibrationSummary>('/v4/calibration-certificates/summary', params);
}

export async function getCalibrationOrganisations(
  options?: {
    showAll?: boolean;
    accountId?: string;
    dueInDays?: number;
    dueDateFrom?: string;
    dueDateTo?: string;
    includeOverdue?: boolean;
    status?: string | string[];
  }
): Promise<CalibrationOrganisation[]> {
  const params: Record<string, string | number | boolean | undefined> = {};
  if (options?.showAll) params.showAll = true;
  if (options?.accountId) params.accountId = options.accountId;
  if (options?.dueInDays) params.dueInDays = options.dueInDays;
  if (options?.dueDateFrom) params.dueDateFrom = options.dueDateFrom;
  if (options?.dueDateTo) params.dueDateTo = options.dueDateTo;
  if (options?.includeOverdue !== undefined) params.includeOverdue = options.includeOverdue;
  if (options?.status) params.status = Array.isArray(options.status) ? options.status.join(',') : options.status;
  const response = await api.get<{ organisations: CalibrationOrganisation[] }>('/v4/calibration-certificates/organisations', params);
  return response.organisations || [];
}

export async function getCalibrationCertificate(id: string): Promise<CalibrationCertificate> {
  const response = await api.get<{ certificate: CalibrationCertificate }>(`/v4/calibration-certificates/${id}`);
  return response.certificate;
}

/**
 * Lock a calibration certificate. V4 endpoint; gated by the V4 session and
 * `calibrationrecords.modify` permission. web_api_server forwards to the
 * existing rest_server /v3/calibrationrecords/:id/lock; the PG mirror updates
 * via CDC.
 */
export async function lockCalibrationCertificate(mongoId: string): Promise<void> {
  await api.put<unknown>(`/v4/calibration-certificates/${mongoId}/lock`, {}, {
    skipAuthRedirect: true,
  });
}

/**
 * Unlock a calibration certificate. Same gate as lock.
 */
export async function unlockCalibrationCertificate(mongoId: string): Promise<void> {
  await api.put<unknown>(`/v4/calibration-certificates/${mongoId}/unlock`, {}, {
    skipAuthRedirect: true,
  });
}

/**
 * Fetch full certificate details from MongoDB (V3 API) for printable view
 * @param mongoId - The MongoDB _id of the calibration record
 */
export async function getCalibrationCertificateDetail(
  mongoId: string
): Promise<CalibrationCertificateDetail> {
  const response = await api.get<{ calibrationrecord: CalibrationCertificateDetail }>(
    `/v4/calibrationrecords/${mongoId}`
  );
  return response.calibrationrecord;
}

// =============================================================================
// Customer Expansion Intelligence API
// =============================================================================

import type {
  ExpansionSummary,
  ExpansionCustomerDetail,
  ExpansionCustomersResponse,
  ExpansionCategory,
} from '../types';

export interface ExpansionCustomersParams {
  sort?: 'expansion_score' | 'risk_score' | 'name' | 'tenure' | 'health_score';
  order?: 'asc' | 'desc';
  category?: ExpansionCategory | 'all';
  search?: string;
  page?: number;
  pageSize?: number;
  refresh?: boolean;
}

export async function getExpansionSummary(refresh = false): Promise<ExpansionSummary> {
  const response = await api.get<{ success: boolean; data: ExpansionSummary }>('/v4/expansion/summary', {
    refresh: refresh ? 'true' : undefined,
  });
  return response.data;
}

export async function getExpansionCustomers(
  params: ExpansionCustomersParams = {}
): Promise<ExpansionCustomersResponse> {
  const { page = 0, pageSize = 25, sort, order, category, search, refresh = false } = params;

  const response = await api.get<{
    success: boolean;
    data: ExpansionCustomersResponse;
  }>('/v4/expansion/customers', {
    limit: pageSize,
    offset: page * pageSize,
    sort: sort || undefined,
    order: order || undefined,
    category: category && category !== 'all' ? category : undefined,
    search: search || undefined,
    refresh: refresh ? 'true' : undefined,
  });

  return response.data;
}

export async function getExpansionCustomerDetail(
  customerId: string,
  refresh = false
): Promise<ExpansionCustomerDetail> {
  const response = await api.get<{ success: boolean; data: ExpansionCustomerDetail }>(
    `/v4/expansion/customers/${customerId}/detail`,
    { refresh: refresh ? 'true' : undefined }
  );
  return response.data;
}

// =============================================================================
// Saved Views API
// =============================================================================

/**
 * Get saved views for a feature
 * Returns empty array on error to prevent page from breaking
 */
export async function getSavedViews(feature: string): Promise<SavedView[]> {
  try {
    const response = await api.get<SavedViewsResponse>('/v4/saved-views', { feature });
    return response.views || [];
  } catch (error) {
    // Log error but don't propagate - saved views are non-critical
    // This prevents redirect loops if the endpoint has auth issues
    console.warn('Failed to load saved views:', error);
    return [];
  }
}

/**
 * Create a new saved view
 */
export async function createSavedView(data: CreateSavedViewInput): Promise<SavedView> {
  const response = await api.post<{ view: SavedView }>('/v4/saved-views', data);
  return response.view;
}

/**
 * Update a saved view
 */
export async function updateSavedView(id: string, data: UpdateSavedViewInput): Promise<SavedView> {
  const response = await api.put<{ view: SavedView }>(`/v4/saved-views/${id}`, data);
  return response.view;
}

/**
 * Delete a saved view
 */
export async function deleteSavedView(id: string): Promise<void> {
  await api.delete(`/v4/saved-views/${id}`);
}

// =============================================================================
// Calibration Certificates Export API
// =============================================================================

/**
 * Export calibration certificates as CSV
 * Returns the CSV file as a Blob for download
 */
export async function exportCalibrationCertificatesCSV(
  filters: CalibrationCertificateFilters = {}
): Promise<Blob> {
  const params: Record<string, string | number | boolean | undefined> = {};

  if (filters.accountId) params.accountId = filters.accountId;
  if (filters.organisationId) params.organisationId = filters.organisationId;
  if (filters.dueInDays) params.dueInDays = filters.dueInDays;
  if (filters.dueDateFrom) params.dueDateFrom = filters.dueDateFrom;
  if (filters.dueDateTo) params.dueDateTo = filters.dueDateTo;
  if (filters.includeOverdue !== undefined) params.includeOverdue = filters.includeOverdue;
  if (filters.status) {
    params.status = Array.isArray(filters.status) ? filters.status.join(',') : filters.status;
  }
  if (filters.search) params.search = filters.search;
  if (filters.locked !== undefined) params.locked = filters.locked;
  if (filters.showAll) params.showAll = true;
  if (filters.columnFilters && Object.values(filters.columnFilters).some(Boolean)) {
    params.columnFilters = JSON.stringify(filters.columnFilters);
  }

  // Build query string
  const queryString = Object.entries(params)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');

  const url = `/api/v4/calibration-certificates/export${queryString ? `?${queryString}` : ''}`;

  // Use fetch directly for blob response (with /api prefix for Vite proxy)
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Export failed: ${response.statusText}`);
  }

  return response.blob();
}

// ---- Certificate Validation (P-780-E §5.9.2 review + statistics) ----

import type {
  CertificateReviewResponse,
  CertificateStatsResponse,
  RegisterPushResponse,
} from '../types';

/** Non-signed certificates in [from, to] triaged per P-780-E §5.9.2. Read-only. */
export async function getCertificateReview(
  range: { from: string; to?: string },
): Promise<CertificateReviewResponse> {
  const params: Record<string, string> = { from: range.from };
  if (range.to) params.to = range.to;
  return api.get<CertificateReviewResponse>('/v4/calibration-certificates/review', params);
}

/** Certificate statistics over [from, to] (all certificates — signed included). */
export async function getCertificateStats(
  range: { from: string; to?: string },
): Promise<CertificateStatsResponse> {
  const params: Record<string, string> = { from: range.from };
  if (range.to) params.to = range.to;
  return api.get<CertificateStatsResponse>('/v4/calibration-certificates/stats', params);
}

/** Report rejected certificates to the F-770 register (dryRun previews without writing). */
export async function pushCertificateRegister(
  body: { certs: string[]; dryRun: boolean; confirmationToken?: string },
): Promise<RegisterPushResponse> {
  return api.post<RegisterPushResponse>('/v4/calibration-certificates/register-push', body);
}

/** Append a rejection annotation to one certificate's comment (before signature). */
export async function annotateCertificate(mongoId: string, comment: string): Promise<void> {
  await api.put(`/v4/calibration-certificates/${mongoId}/annotate`, { comment }, { skipAuthRedirect: true });
}
